import type { QueryKey } from '@tanstack/react-query';
import { SALES_GROUP_QUERY_KEY } from 'src/hooks/api/useGetSalesGroup';
import { MarketplacePaymentState } from 'src/WebApiController';

/**
 * Pure payment-target/invalidation helpers shared by the marketplace payment
 * dialog (POS-9097). Extracted so the group-vs-single-sale keying is unit
 * testable without rendering the (context-heavy) dialog.
 *
 * A single retail sale is targeted when `saleId != null`; otherwise the payment
 * targets the multi-sale group identified by `salesGroupId`. Exactly one of the
 * two is set by the dialog's props.
 */

/** Target for `useApplyExternalPayment` (discriminated union — one key only). */
export type ApplyPaymentTarget = { saleId: number } | { salesGroupId: string };

/**
 * Target for the Adyen session / stored-method requests, which carry BOTH
 * `saleId` and `salesGroupId` fields (exactly one non-null).
 */
export type AdyenPaymentTarget = {
  salesGroupId: string | null;
  saleId: number | null;
};

/** True when the dialog is paying down a TRUE single sale rather than a group. */
export const isSalePaymentMode = (saleId: number | null | undefined): boolean =>
  saleId != null;

/**
 * `useApplyExternalPayment` accepts either `{ saleId }` (→ ?saleId=) or
 * `{ salesGroupId }` (→ ?salesGroupId=). Returns the single-key variant.
 */
export const resolveApplyPaymentTarget = (
  saleId: number | null | undefined,
  salesGroupId: string | null | undefined
): ApplyPaymentTarget =>
  saleId != null ? { saleId } : { salesGroupId: salesGroupId! };

/**
 * Adyen `createPaymentSession` / `submitPaymentWithStoredMethod` requests carry
 * both `saleId` and `salesGroupId`; exactly one is non-null. The same
 * `salesGroupId` value is passed to `submitPaymentResult` (null for a single
 * sale — that endpoint resolves the target by MerchantReference).
 */
export const resolveAdyenPaymentTarget = (
  saleId: number | null | undefined,
  salesGroupId: string | null | undefined
): AdyenPaymentTarget =>
  saleId != null
    ? { salesGroupId: null, saleId }
    : { salesGroupId: salesGroupId ?? null, saleId: null };

/**
 * Query keys to invalidate after a successful payment. Single-sale mode
 * refreshes the sale's own detail/line-item queries; group mode refreshes the
 * group payload and its member-sales list. The two are mutually exclusive — a
 * single-sale payment never invalidates the group keys and vice versa.
 */
export const resolvePaymentInvalidationKeys = (
  saleId: number | null | undefined,
  salesGroupId: string | null | undefined
): QueryKey[] =>
  saleId != null
    ? [
        ['getRetailSaleDetails', saleId],
        ['getRetailSaleLineItems', saleId],
      ]
    : [
        [SALES_GROUP_QUERY_KEY, salesGroupId],
        ['getSalesInSalesGroup', salesGroupId],
      ];

/** A marketplace payment line as far as the outstanding computation cares. */
type OutstandingMkpLine = {
  state: MarketplacePaymentState;
  amt?: { amt: number } | null;
};

/**
 * Outstanding balance for a single retail sale (POS-9097): the sum of its
 * still-Unpaid marketplace payment lines. This deliberately matches the coverage
 * set `computeProportionalCoverages` builds (Unpaid lines with amt > 0), so a full
 * payment's amount equals Σ(coverages) — satisfying the backend's exact
 * "amount === sum(coverages)" guard. Used both by the SaleDetails footer's
 * "Add Payment" action and by the marketplace-payments page "Pay" row action.
 */
export const getSaleOutstandingAmt = (
  mkpLines: OutstandingMkpLine[] | null | undefined
): number =>
  (mkpLines ?? [])
    .filter(
      (line) =>
        line.state === MarketplacePaymentState.Unpaid &&
        (line.amt?.amt ?? 0) > 0
    )
    .reduce((sum, line) => sum + (line.amt?.amt ?? 0), 0);

/**
 * Whether a sale-level "Add Payment" action should be shown, mirroring the group
 * PaymentSection gate (POS-8632): a retail sale with an outstanding balance AND
 * update permission. Pure so it can be unit tested without rendering the
 * context-heavy SaleDetails footer.
 */
export const canAddSalePayment = ({
  isRetail,
  outstandingAmt,
  canUpdateSale,
}: {
  isRetail: boolean;
  outstandingAmt: number;
  canUpdateSale: boolean;
}): boolean => isRetail && outstandingAmt > 0 && canUpdateSale;
